/**
 * Shared task "box" card used wherever we surface a task summary — the
 * Tehtävät kanban columns and any dialog that lists tasks (plot detail,
 * headsection detail, etc.). Keeping this in one place prevents the kanban
 * visual and the dialog lists from drifting apart.
 *
 * The card itself is self-contained. Callers that need extra wrapping
 * (e.g. the kanban's virtualiser) wrap the rendered card from outside.
 */

import { useEffect, useRef, useState, type MouseEvent } from "react";
import { TaskStatus, TaskType, type SubtaskProgress, type TaskExtended } from "@/types";
import { Card } from "@/components/ui/card";
import {
  ProjectIcon,
  SpaceIcon,
  PlotIcon,
  DateIcon,
  UserIcon,
  RecurrenceIcon,
  CheckmarkIcon,
} from "@/lib/icons";
import { getDueDateInfo, formatDateFi } from "@/lib/format";
import { TASK_STATUS_COLORS, TASK_BORDER_BY_STATUS } from "@/lib/taskStatusMeta";
import congridLogo from "@/assets/CongridLogo.png";
import { formatResponsible } from "@/lib/responsibleUser";
import type { TaskCompletionToggle } from "@/hooks/useTaskCompletionToggle";
import {
  ConfettiBurst,
  CONFETTI_MS,
  useCelebration,
} from "@/components/tasks/CompletionEffects";
import { cn } from "@/lib/utils";

/** Lowercase first letter, rest unchanged. */
export function lowerFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
}

export function getLocationPath(
  task: TaskExtended,
  plotNameById?: Map<string, string>,
): string {
  const plotName = task.plotId ? plotNameById?.get(task.plotId) : undefined;
  const hierarchy = task.location?.hierarchyPath;
  const locPath =
    hierarchy && hierarchy.length > 0
      ? hierarchy.join(" > ")
      : task.location?.name || "";
  return locPath || plotName || "";
}

export function getAssigneeName(task: TaskExtended): string | null {
  if (!task.responsibleUser) return null;
  // Substitution-aware: renders "B (A)" while the responsible user is
  // substituted, otherwise just the responsible user's name.
  return formatResponsible(task.responsibleUser) || null;
}

/**
 * Subtask progress strip: count row + stacked progress bar. Colours come
 * from the central task-status palette so every consumer stays in sync.
 */
export function TaskProgress({
  progress,
  isCongrid,
  showWhenEmpty = false,
}: {
  progress: SubtaskProgress | null | undefined;
  isCongrid?: boolean;
  showWhenEmpty?: boolean;
}) {
  if (!progress || (progress.total === 0 && !showWhenEmpty)) return null;

  const {
    inProgress: inProgressCount,
    completed: completedCount,
    problem: problemCount,
    total,
  } = progress;
  const completedPercent = (completedCount / total) * 100;
  const inProgressPercent = (inProgressCount / total) * 100;
  const problemPercent = (problemCount / total) * 100;

  return (
    <div className="space-y-1 pt-2 border-t">
      <div
        className={`flex h-2 w-full overflow-hidden rounded-full ${TASK_STATUS_COLORS[TaskStatus.PENDING].track}`}
      >
        {completedPercent > 0 && (
          <div
            className={`h-full transition-all duration-300 ${TASK_STATUS_COLORS[TaskStatus.COMPLETED].bar}`}
            style={{ width: `${completedPercent}%` }}
          />
        )}
        {inProgressPercent > 0 && (
          <div
            className={`h-full transition-all duration-300 ${TASK_STATUS_COLORS[TaskStatus.IN_PROGRESS].bar}`}
            style={{ width: `${inProgressPercent}%` }}
          />
        )}
        {problemPercent > 0 && (
          <div
            className={`h-full transition-all duration-300 ${TASK_STATUS_COLORS[TaskStatus.PROBLEM].bar}`}
            style={{ width: `${problemPercent}%` }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Two sizes, because the two placements have different room to work with.
 *
 * `card` is tucked into a dense summary card, so it stays small on a desktop
 * pointer and grows to a finger-sized target on mobile. Its media conditions
 * mirror MOBILE_QUERY in hooks/useIsMobile.ts — the mobile layout is routed
 * when the viewport's *shorter* side is under 768px, and a plain `md:`
 * breakpoint would miss a phone held in landscape, which is wide but short.
 *
 * `dialog` sits in the open task's own panel with space around it, so it is a
 * flat 40px everywhere.
 */
const TOGGLE_SIZES = {
  card: {
    button:
      "h-7 w-7 max-md:h-9 max-md:w-9 [@media(max-height:767px)]:h-9 [@media(max-height:767px)]:w-9",
    icon: "h-4 w-4 max-md:h-5 max-md:w-5 [@media(max-height:767px)]:h-5 [@media(max-height:767px)]:w-5",
  },
  dialog: { button: "h-10 w-10", icon: "h-5 w-5" },
} as const;

/**
 * Round checkmark control for main tasks with no subtasks, toggling between
 * `COMPLETED` and `PENDING`. Positioned by the caller through `className` —
 * on the summary card that is the top-right corner, where the Congrid logo
 * goes on inspection tasks (the two never share a card, since Congrid task
 * status is not ours to set); in the task dialog it is the bottom-right of
 * the Alitehtävät panel.
 *
 * The displayed state flips on click and reconciles from the props once the
 * mutation lands. `versionRef` carries the version returned by the previous
 * mutation so a second click doesn't send a stale one; while a click is in
 * flight the props are older than what the user sees, so reconciliation is
 * held back until it resolves.
 *
 * `onCompleted` fires only when a click actually persisted a move to
 * `COMPLETED`, which is what the caller hangs its completion effect on. A
 * failed mutation or a move back to `PENDING` stays silent.
 */
export function CompletionToggle({
  taskId,
  status,
  version,
  onToggle,
  onCompleted,
  size = "card",
  className,
}: {
  taskId: string;
  status: TaskStatus;
  version: number;
  onToggle: TaskCompletionToggle;
  onCompleted?: () => void;
  size?: keyof typeof TOGGLE_SIZES;
  /** Placement classes. The `absolute` itself is part of the base styling. */
  className?: string;
}) {
  const [displayStatus, setDisplayStatus] = useState(status);
  const versionRef = useRef(version);
  const busyRef = useRef(false);

  useEffect(() => {
    if (!busyRef.current) {
      setDisplayStatus(status);
      versionRef.current = version;
    }
  }, [status, version]);

  const isCompleted = displayStatus === TaskStatus.COMPLETED;
  const label = isCompleted ? "Merkitse aloittamattomaksi" : "Merkitse valmiiksi";

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (busyRef.current) return;
    const next = isCompleted ? TaskStatus.PENDING : TaskStatus.COMPLETED;
    busyRef.current = true;
    setDisplayStatus(next);
    const newVersion = await onToggle(taskId, next, versionRef.current);
    if (typeof newVersion === "number") {
      versionRef.current = newVersion;
      if (next === TaskStatus.COMPLETED) {
        onCompleted?.();
      }
    } else {
      setDisplayStatus(status);
    }
    busyRef.current = false;
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={isCompleted}
      aria-label={label}
      title={label}
      className={cn(
        "absolute flex items-center justify-center rounded-full border-2 shadow-sm transition-colors",
        TOGGLE_SIZES[size].button,
        isCompleted
          ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700"
          : "border-blue-400 bg-white text-blue-500 hover:border-blue-600 hover:bg-blue-50 hover:text-blue-700",
        className,
      )}
    >
      <CheckmarkIcon className={TOGGLE_SIZES[size].icon} strokeWidth={3} />
    </button>
  );
}

export function DueDateCountdown({ dueDate }: { dueDate: string }) {
  const info = getDueDateInfo(dueDate);
  if (!info) return null;
  return (
    <p className={`text-xs font-medium ${info.colorClass}`}>{info.text}</p>
  );
}

interface TaskSummaryCardProps {
  task: TaskExtended;
  /**
   * Map of project id → project name. Pass to render the project name
   * line; omit (e.g. from a project-scoped dialog) to skip it.
   */
  projectNameById?: Map<string, string>;
  /**
   * Map of plot id → plot name. Used as a fallback when `task.location`
   * has no hierarchy path. Optional — omit in contexts that don't need
   * the plot-name fallback.
   */
  plotNameById?: Map<string, string>;
  onClick?: () => void;
  /**
   * Enables the checkmark toggle on subtaskless main tasks.
   * Supply it from `useTaskCompletionToggle`, wired to the caller's own list
   * refetch. Omit it in read-only contexts.
   */
  onToggleCompleted?: TaskCompletionToggle;
}

/**
 * Rich task "box" used in the Tehtävät kanban and shared task-list
 * dialogs. Shows the task title grouped under its section / PP / personal
 * heading, the PP code + name, metadata (project, location, due date,
 * assignee), a due-date countdown for non-completed tasks, and a subtask
 * progress bar.
 */
export function TaskSummaryCard({
  task,
  projectNameById,
  plotNameById,
  onClick,
  onToggleCompleted,
}: TaskSummaryCardProps) {
  const isCongrid =
    task.taskType === TaskType.INSPECTION_MAIN_TASK ||
    !!task.externalInspectionPlatformInspectionId;
  const borderClass = TASK_BORDER_BY_STATUS[task.status] || "border-l-slate-300";
  // Confetti for a subtaskless task finished straight from the board. A task
  // with subtasks is only ever completed from the dialog, which runs its own.
  const [confettiRun, fireConfetti] = useCelebration(CONFETTI_MS);

  const pp = task.procurementPackage || task.section?.procurementPackage;
  const workPackage = task.sitedriveMetadata?.sitedriveWorkPackage;
  const parentWorkPackage = task.sitedriveMetadata?.sitedriveParentWorkPackage;
  const grandparentWorkPackage =
    task.sitedriveMetadata?.sitedriveGrandparentWorkPackage;
  const ancestry = [grandparentWorkPackage, parentWorkPackage].filter(Boolean);
  // Show whatever is available: ancestry (grandparent, parent) followed by the
  // work-package level in parentheses. When only the work package is present,
  // show it on its own without empty parentheses.
  const ancestryText = ancestry.join(", ");
  const sitedrivePath = workPackage
    ? ancestryText
      ? `${ancestryText} (${workPackage})`
      : workPackage
    : ancestryText;
  const projectName = task.projectId
    ? projectNameById?.get(task.projectId)
    : undefined;
  const locationPath = getLocationPath(task, plotNameById);
  const assigneeName = getAssigneeName(task);
  // Congrid inspection tasks are excluded: their status belongs to Congrid and
  // cannot legally be set through our API user.
  const showCompletionToggle =
    !!onToggleCompleted &&
    !isCongrid &&
    task.viewerCanWrite !== false &&
    (task.subtaskProgress?.total ?? 0) === 0;

  return (
    <Card
      className={`relative p-4 cursor-pointer hover:bg-accent/50 transition-colors border-l-4 ${borderClass}`}
      onClick={onClick}
    >
      {isCongrid && (
        <img
          src={congridLogo}
          alt="Congrid"
          className="absolute top-2 right-2 h-5 w-auto"
        />
      )}
      {showCompletionToggle && onToggleCompleted && (
        <CompletionToggle
          taskId={task.id}
          status={task.status}
          version={task.version}
          onToggle={onToggleCompleted}
          onCompleted={fireConfetti}
          className="top-2 right-2"
        />
      )}
      <ConfettiBurst runId={confettiRun} className="z-20" />
      {/* pl-2 indents the content below to line up with the chip text
          (chips have px-2); the title row cancels it with -ml-2 so the grey
          highlights stay flush with the card edge */}
      <div className="space-y-3 pl-2">
        {/* Row 1: task title on its own grey highlight. pr-10 keeps it clear of
            the Congrid logo / completion toggle in the corner above. */}
        <div className="flex items-center gap-1.5 pr-10 -ml-2">
          <h3 className="inline-block rounded bg-muted px-2 py-0.5 text-base font-medium leading-snug text-foreground">
            {task.title}
          </h3>
          {task.isRecurring && (
            <RecurrenceIcon
              className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
              aria-label="Toistuva tehtävä"
            />
          )}
        </div>

        {/* Inspection tasks are always section-level, so show the section
            code + name instead of the procurement package. Otherwise the
            procurement package code + name, plus section name for section
            tasks — black, not bold */}
        <div className="flex items-center gap-2 text-sm text-foreground">
          {isCongrid && task.section ? (
            <>
              {task.section.code && (
                <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                  {task.section.code}
                </span>
              )}
              <span className="truncate">
                {task.section.name || "Tuntematon työvaihe"}
              </span>
            </>
          ) : pp ? (
            <>
              <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                {pp.code}
              </span>
              <span className="truncate">
                {pp.name || "Tuntematon paketti"}
                {task.section &&
                  task.section.name !== pp.name &&
                  `, ${task.section.name}`}
              </span>
            </>
          ) : task.taskType === TaskType.INTERNAL_MAIN_TASK ? (
            <>
              <span className="py-1.5 font-mono inline-flex items-center gap-1 bg-slate-100 px-1.5 rounded text-muted-foreground">
                <UserIcon className="h-3 w-3 shrink-0" />
              </span>
              <span className="text-muted-foreground truncate">
                Henkilökohtainen tehtävä
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">N/A</span>
          )}
        </div>

        {/* Info row: grandparent, parent ancestry plus the work-package level
            for section tasks (only when present), or PP name, or personal for
            non-section tasks. */}
        {(task.section ? sitedrivePath.length > 0 : true) && (
          <div className="text-sm text-foreground">
            {task.section
              ? sitedrivePath
              : pp
                ? pp.name || "Tuntematon paketti"
                : "Henkilökohtainen"}
          </div>
        )}

        {/* Project, plot/location, due date, assignee */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {projectName && (
            <span className="flex items-center gap-1 min-w-0">
              <ProjectIcon className="h-3 w-3 shrink-0" />
              <span className="truncate">{projectName}</span>
            </span>
          )}
          {task.plot?.name && (
            <span className="flex items-center gap-1 min-w-0">
              <PlotIcon className="h-3 w-3 shrink-0" />
              <span className="truncate">{task.plot.name}</span>
            </span>
          )}
          {locationPath && (
            <span className="flex items-center gap-1 min-w-0 text-foreground">
              <SpaceIcon className="h-3 w-3 shrink-0" />
              {/* bg-amber-100 = highlight colour. Change this class to
                  recolour (e.g. bg-yellow-100, bg-sky-100, bg-emerald-100). */}
              <span className="truncate rounded bg-stone-100 px-1.5 py-0.5">
                {locationPath}
              </span>
            </span>
          )}
          {assigneeName && (
            <span className="flex items-center gap-1 min-w-0">
              <UserIcon className="h-3 w-3 shrink-0" />
              <span className="truncate">{assigneeName}</span>
            </span>
          )}
          {task.dueDate && (
            <span className="flex items-center gap-1">
              <DateIcon className="h-3 w-3" />
              <span>{formatDateFi(task.dueDate)}</span>
            </span>
          )}
          {task.status !== TaskStatus.COMPLETED && task.dueDate && (
            <DueDateCountdown dueDate={task.dueDate} />
          )}
        </div>

        <TaskProgress progress={task.subtaskProgress} isCongrid={isCongrid} />
      </div>
    </Card>
  );
}
